import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface IndustryContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  primaryCta: string
  secondaryCta: string
  badges: string[]
  pillarsEyebrow: string
  pillarsTitle: string
  pillarsBody: string
  pillars: Pillar[]
  howEyebrow: string
  howTitle: string
  steps: Step[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', IndustryContent> = {
  en: {
    metaTitle: 'Identity & access for financial services',
    metaDescription:
      'Workforce, privileged, and customer identity for banks, insurers, and payment institutions. PCI-DSS, SWIFT CSP, KVKK, and ISO 27001-ready.',
    heroEyebrow: 'Industries · Financial Services',
    heroTitle: ['Identity for banks,', 'built for regulators.'],
    heroBody:
      'Workforce, privileged, and customer identity on one platform, engineered for PCI-DSS, SWIFT CSP, KVKK, GDPR, and ISO 27001. From the branch teller to the SWIFT operator, governed and proven.',
    primaryCta: 'Start free trial →',
    secondaryCta: 'Talk with our experts',
    badges: [
      'PCI-DSS, SWIFT CSP-aligned',
      'Data residency in Turkey / EU',
      'Trusted by Ziraat Bankası, Odeabank',
    ],
    pillarsEyebrow: 'What you get',
    pillarsTitle: 'What financial services teams get with Monofor.',
    pillarsBody:
      'Banks, insurers, and payment institutions run on identity. Monofor brings workforce, privileged, and customer identity into one regulated, auditable platform.',
    pillars: [
      {
        title: 'Built for regulators, not against them',
        description:
          'PCI-DSS, SWIFT CSP, KVKK, GDPR, ISO 27001, SOC 2: Monofor produces the artifacts your auditors and regulators ask for, on schedule.',
        bullets: [
          'Pre-built control mappings per framework',
          'Immutable, exportable audit logs',
          'Separation-of-duties enforcement',
          'Data residency in Turkey, EU, or your own region',
        ],
      },
      {
        title: 'Branch, headquarters, and trading desk',
        description:
          'Authentication everywhere your business actually operates, from the branch teller through to the trader on a market terminal.',
        bullets: [
          'MFA on the Windows desktop and OS logon',
          'Branch-aware policy and risk scoring',
          'High-availability across data centers',
          'Offline OTP for disconnected branches',
        ],
      },
      {
        title: 'Privileged access for core banking',
        description:
          'Database administrators, payment-system operators, and SWIFT users get brokered, recorded, and time-bound access, not standing privilege.',
        bullets: [
          'Brokered access to Oracle, MSSQL, and payment systems',
          'Full session recording with keystroke transcription',
          'Just-in-time elevation tied to change tickets',
          'SoD enforcement on payment workflows',
        ],
      },
      {
        title: 'Customer identity without the data sprawl',
        description:
          'Manage workforce, partner, and external identities in one platform. Apply the same policy lens to retail customers as to internal staff.',
        bullets: [
          'B2B and B2C identity in one product',
          'Customer MFA without third-party hosting',
          'Consent and preference management',
          'Encrypted storage with HSM-backed keys',
        ],
      },
    ],
    howEyebrow: 'How it works',
    howTitle: 'From desktop to data center to regulator.',
    steps: [
      {
        title: 'Lock down the desktop and branch',
        description:
          'Deploy MFA on Windows logon, RDP, and VPN. Risk-aware policies catch branch-side anomalies before they spread.',
      },
      {
        title: 'Broker every privileged session',
        description:
          'Route DBA, SWIFT, and payment-system access through Monopam. Vault credentials. Record every session.',
      },
      {
        title: 'Prove it to the regulator',
        description:
          'Export pre-built reports for PCI-DSS, ISO 27001, KVKK, and your local supervisory authority, on the cadence they require.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      {
        label: 'Privileged session recording',
        description: 'The evidence backbone behind every audit response.',
        href: '/pam/session-recording',
      },
      {
        label: 'Identity governance',
        description: 'Entitlement mapping and access reviews for core banking.',
        href: '/sync/governance',
      },
      {
        label: 'Audit & compliance',
        description: 'Use case: making auditors and regulators happy.',
        href: '/solutions/audit-compliance',
      },
    ],
  },
  tr: {
    metaTitle: 'Finansal hizmetler için kimlik ve erişim',
    metaDescription:
      'Bankalar, sigorta şirketleri ve ödeme kuruluşları için çalışan, ayrıcalıklı ve müşteri kimliği. PCI-DSS, SWIFT CSP, KVKK ve ISO 27001’e hazır.',
    heroEyebrow: 'Sektörler · Finansal Hizmetler',
    heroTitle: ['Bankalar için kimlik,', 'regülatörler için tasarlandı.'],
    heroBody:
      'Çalışan, ayrıcalıklı ve müşteri kimliği tek platformda; PCI-DSS, SWIFT CSP, KVKK, GDPR ve ISO 27001 için tasarlandı. Şube gişesinden SWIFT operatörüne kadar yönetişim altında ve kanıtlanabilir.',
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Uzmanlarımızla görüşün',
    badges: [
      'PCI-DSS ve SWIFT CSP ile uyumlu',
      'Türkiye / AB’de veri yerleşimi',
      'Ziraat Bankası ve Odeabank’ın tercihi',
    ],
    pillarsEyebrow: 'Neler elde edersiniz',
    pillarsTitle: 'Finansal hizmetler ekiplerinin Monofor ile elde ettikleri.',
    pillarsBody:
      'Bankalar, sigorta şirketleri ve ödeme kuruluşları kimlik üzerinde çalışır. Monofor; çalışan, ayrıcalıklı ve müşteri kimliğini regülasyona uygun, denetlenebilir tek platformda birleştirir.',
    pillars: [
      {
        title: 'Regülatörlere karşı değil, regülatörler için tasarlandı',
        description:
          'PCI-DSS, SWIFT CSP, KVKK, GDPR, ISO 27001, SOC 2: Monofor, denetçilerinizin ve regülatörlerin istediği kanıtları zamanında üretir.',
        bullets: [
          'Çerçeve başına hazır kontrol eşlemeleri',
          'Değiştirilemez, dışa aktarılabilir denetim logları',
          'Görevler ayrılığı (SoD) uygulaması',
          'Türkiye, AB veya kendi bölgenizde veri yerleşimi',
        ],
      },
      {
        title: 'Şube, genel müdürlük ve işlem masası',
        description:
          'İşinizin gerçekten yürüdüğü her yerde kimlik doğrulama: şube gişesinden piyasa terminalindeki trader’a kadar.',
        bullets: [
          'Windows masaüstünde ve işletim sistemi oturumunda MFA',
          'Şubeye duyarlı politika ve risk puanlama',
          'Veri merkezleri arasında yüksek erişilebilirlik',
          'Bağlantısız şubeler için çevrimdışı OTP',
        ],
      },
      {
        title: 'Ana bankacılık için ayrıcalıklı erişim',
        description:
          'Veritabanı yöneticileri, ödeme sistemi operatörleri ve SWIFT kullanıcıları; kalıcı ayrıcalık yerine aracılı, kayıtlı ve süreli erişim alır.',
        bullets: [
          'Oracle, MSSQL ve ödeme sistemlerine aracılı erişim',
          'Tuş vuruşu dökümüyle tam oturum kaydı',
          'Değişiklik kayıtlarına bağlı tam zamanında yetki yükseltme',
          'Ödeme akışlarında görevler ayrılığı (SoD) uygulaması',
        ],
      },
      {
        title: 'Veri dağınıklığı olmadan müşteri kimliği',
        description:
          'Çalışan, iş ortağı ve dış kimlikleri tek platformda yönetin. İç personele uyguladığınız politika merceğini bireysel müşterilere de uygulayın.',
        bullets: [
          'Tek üründe B2B ve B2C kimlik',
          'Üçüncü taraf barındırma olmadan müşteri MFA’sı',
          'Rıza ve tercih yönetimi',
          'HSM destekli anahtarlarla şifreli depolama',
        ],
      },
    ],
    howEyebrow: 'Nasıl çalışır',
    howTitle: 'Masaüstünden veri merkezine, oradan regülatöre.',
    steps: [
      {
        title: 'Masaüstünü ve şubeyi güvenceye alın',
        description:
          'Windows oturumu, RDP ve VPN’de MFA devreye alın. Riske duyarlı politikalar şube tarafındaki anomalileri yayılmadan yakalar.',
      },
      {
        title: 'Her ayrıcalıklı oturumu aracılayın',
        description:
          'DBA, SWIFT ve ödeme sistemi erişimini Monopam üzerinden yönlendirin. Kimlik bilgilerini kasada tutun. Her oturumu kaydedin.',
      },
      {
        title: 'Regülatöre kanıtlayın',
        description:
          'PCI-DSS, ISO 27001, KVKK ve yerel denetim otoriteniz için hazır raporları, istedikleri sıklıkta, dışa aktarın.',
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      {
        label: 'Ayrıcalıklı oturum kaydı',
        description: 'Her denetim yanıtının arkasındaki kanıt omurgası.',
        href: '/pam/session-recording',
      },
      {
        label: 'Kimlik yönetişimi',
        description: 'Ana bankacılık için yetki eşleme ve erişim gözden geçirmeleri.',
        href: '/sync/governance',
      },
      {
        label: 'Denetim ve uyum',
        description: 'Kullanım senaryosu: denetçileri ve regülatörleri memnun etmek.',
        href: '/solutions/audit-compliance',
      },
    ],
  },
}

type Props = { locale: string }

export default function FinancialServicesPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/solutions/industries/financial-services"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: '/contact' }}
          badges={c.badges}
        />
        <SolutionPillars
          eyebrow={c.pillarsEyebrow}
          title={<>{c.pillarsTitle}</>}
          body={c.pillarsBody}
          pillars={c.pillars}
        />
        <ProductHowItWorks
          eyebrow={c.howEyebrow}
          title={<>{c.howTitle}</>}
          steps={c.steps}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
